<script setup lang="ts">
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import Backend from '@/models/backend.ts'
import { useChatStore } from '@/stores/chatStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores
 */
const backend = new Backend()
const chatStore = useChatStore()

/**
 * Variables
 */
const message = ref('')
const errorMessage = ref('')
const initialRoomId = chatStore.activeRoomId
const initialViewportHeight = ref(window.innerHeight)
const isKeyboardOpen = ref(false)
const selectedImage = ref<File | null>(null)
const imagePreviewUrl = ref('')
const isDragging = ref(false)
const isUploading = ref(false)

/**
 * Keyboard detection for mobile
 */
const handleViewportResize = () => {
  if (window.innerWidth < 768) {
    const currentHeight = window.innerHeight
    const heightDifference = initialViewportHeight.value - currentHeight
    isKeyboardOpen.value = heightDifference > 150 // Threshold for keyboard detection
  }
}

/**
 * Lifecycle Hooks
 */
onMounted(async () => {
  await nextTick()
  if (chatStore.activeRoomId) {
    message.value = chatStore.getDraftMessage(chatStore.activeRoomId)
  }
  
  // Listen for viewport changes to detect keyboard
  window.addEventListener('resize', handleViewportResize)
  initialViewportHeight.value = window.innerHeight
})

// Save draft message before unmounting component
onBeforeUnmount(() => {
  if (chatStore.activeRoomId && message.value.trim()) {
    chatStore.setDraftMessage(chatStore.activeRoomId, message.value)
  }
  
  // Clean up resize listener
  window.removeEventListener('resize', handleViewportResize)
})

// Watch for changes in activeRoomId to save current draft and load the corresponding draft message
watch(() => chatStore.activeRoomId, (newRoomId, oldRoomId) => {
  // Skip the initial immediate call with undefined oldRoomId if there's no real change
  if (oldRoomId === undefined && newRoomId === initialRoomId) {
    return
  }
  
  // Save draft for previous room if needed
  if (oldRoomId && message.value.trim()) {
    chatStore.setDraftMessage(oldRoomId, message.value)
  }
  
  // Load draft for new room
  if (newRoomId) {
    message.value = chatStore.getDraftMessage(newRoomId)
  } else {
    message.value = ''
  }
}, { immediate: true })

// Debounced save of draft message when it changes
let saveTimeout: ReturnType<typeof setTimeout> | null = null
watch(message, (newMessage) => {
  if (chatStore.activeRoomId) {
    // Clear any pending saves
    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }
    
    // Debounce save operation (300ms delay)
    saveTimeout = setTimeout(() => {
      // Only save non-empty drafts
      if (newMessage.trim()) {
        chatStore.setDraftMessage(chatStore.activeRoomId, newMessage)
      } else {
        // If message is empty, clear any existing draft
        chatStore.clearDraftMessage(chatStore.activeRoomId)
      }
    }, 300)
  }
})

/**
 * Function to handle form submission
 */
const sendMessage = () => {
  if (message.value.trim() || selectedImage.value) {
    errorMessage.value = '' // Clear previous error message
    isUploading.value = true

    // Close mobile keyboard by blurring the active element only on mobile devices
    if (document.activeElement instanceof HTMLElement && window.innerWidth < 768) {
      document.activeElement.blur()
      // Prevent scroll jumping by scrolling to a stable position
      window.scrollTo(0, 0)
    }

    const sendPromise = backend.postMessageInRoom(
      chatStore.activeRoomId,
      message.value,
      selectedImage.value || undefined
    )

    sendPromise
      .then((response: any) => {
        chatStore.setMessage(response.data.data)
        // Clear the input after sending
        message.value = ''
        clearImage()
        // Also clear the draft message in store
        chatStore.clearDraftMessage(chatStore.activeRoomId)

        // Re-focus the textarea on desktop
        if (window.innerWidth >= 768) {
          nextTick(() => {
            const textareaElement = document.querySelector('textarea')
            if (textareaElement) {
              textareaElement.focus()
            }
          })
        }
      })
      .catch((error) => {
        logger.error(error.response?.data || error)

        // Check if this is a 403 error (player has muted you)
        if (error.response?.status === 403) {
          errorMessage.value = t('communication.chat.errors.communicationBlocked')
        } else if (error.response?.data?.data?.[0]?.message) {
          errorMessage.value = error.response.data.data[0].message
        } else {
          errorMessage.value = t('communication.chat.errors.sendFailed')
        }
      })
      .finally(() => {
        isUploading.value = false
      })
  }
}

/**
 * Handle paste event for images
 */
const handlePaste = (e: ClipboardEvent) => {
  const items = e.clipboardData?.items
  if (!items) return

  for (let i = 0; i < items.length; i++) {
    if (items[i].type.indexOf('image') !== -1) {
      e.preventDefault()
      const file = items[i].getAsFile()
      if (file) {
        processImageFile(file)
      }
      break
    }
  }
}

/**
 * Handle drag over event
 */
const handleDragOver = (e: DragEvent) => {
  e.preventDefault()
  isDragging.value = true
}

/**
 * Handle drag leave event
 */
const handleDragLeave = (e: DragEvent) => {
  e.preventDefault()
  isDragging.value = false
}

/**
 * Handle drop event
 */
const handleDrop = (e: DragEvent) => {
  e.preventDefault()
  isDragging.value = false

  const files = e.dataTransfer?.files
  if (!files || files.length === 0) return

  // Only take the first image file
  for (let i = 0; i < files.length; i++) {
    if (files[i].type.indexOf('image') !== -1) {
      processImageFile(files[i])
      break
    }
  }
}

/**
 * Process image file
 */
const processImageFile = (file: File) => {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    errorMessage.value = t('communication.chat.errors.onlyImages')
    return
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = t('communication.chat.errors.imageTooLarge')
    return
  }

  // Create preview URL and check dimensions
  const reader = new FileReader()
  reader.onload = (e) => {
    const img = new Image()
    img.onload = () => {
      // Validate dimensions (max 2000x2000)
      if (img.width > 2000 || img.height > 2000) {
        errorMessage.value = t('communication.chat.errors.imageDimensionsTooLarge')
        return
      }

      // Image is valid
      selectedImage.value = file
      imagePreviewUrl.value = e.target?.result as string
      errorMessage.value = ''
    }
    img.onerror = () => {
      errorMessage.value = t('communication.chat.errors.imageLoadFailed')
    }
    img.src = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

/**
 * Clear selected image
 */
const clearImage = () => {
  selectedImage.value = null
  imagePreviewUrl.value = ''
  errorMessage.value = ''
}

/**
 * Open file picker for mobile
 */
const openFilePicker = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.onchange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    if (file) {
      processImageFile(file)
    }
  }
  input.click()
}

/**
 * Handle touch events on mobile
 */
const handleTouchEnd = (e: Event) => {
  e.preventDefault()
  sendMessage()
}
</script>

<template>
  <div class="relative">
    <!-- Error Message - positioned above image preview -->
    <div class="absolute error-message w-full flex justify-center">
      <div v-if="errorMessage.length > 0" class="rounded-t bg-red-500 text-white px-4 py-2">
        {{ errorMessage }}
      </div>
    </div>

    <!-- Image Preview - positioned above the input -->
    <div v-if="imagePreviewUrl" class="absolute image-preview-container">
      <div class="bg-slate-800 rounded-t p-2 shadow-lg">
        <div class="relative inline-block">
          <img :src="imagePreviewUrl" alt="Preview" class="max-w-xs max-h-32 rounded" />
          <button
            type="button"
            @click="clearImage"
            class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
          >
            ×
          </button>
        </div>
      </div>
    </div>
    <StandardCard>
      <form
        @submit.prevent="sendMessage"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
        :class="{ 'drag-over': isDragging }"
        class="relative"
      >
        <div class="flex items-center space-x-2">
          <textarea
            v-model="message"
            :placeholder="t('communication.chat.placeholder')"
            class="flex-grow p-2 rounded bg-slate-900 text-white focus:outline-none focus:ring-0 border-0"
            rows="2"
            @keydown.enter.exact.prevent="sendMessage"
            @paste="handlePaste"
            style="overflow: hidden; resize: none"
          ></textarea>

          <!-- Attach image button -->
          <button
            type="button"
            @click="openFilePicker"
            class="p-2 text-slate-400 hover:text-slate-200 transition-colors"
            :title="t('communication.chat.attachImage')"
          >
            <span class="material-symbols-outlined" style="font-size: 24px;">attach_file</span>
          </button>

          <ActionButton
            type="submit"
            @touchend.prevent="handleTouchEnd"
            :disabled="isUploading"
            class="hidden md:block"
          >
            {{ isUploading ? t('communication.chat.sending') : t('communication.chat.send') }}
          </ActionButton>
        </div>

        <!-- Drop overlay -->
        <div
          v-if="isDragging"
          class="absolute inset-0 bg-blue-500 bg-opacity-20 border-2 border-dashed border-blue-500 rounded flex items-center justify-center pointer-events-none"
        >
          <div class="text-blue-500 font-bold text-lg">{{ t('communication.chat.dropImageHere') }}</div>
        </div>
      </form>
    </StandardCard>
  </div>
</template>

<style scoped lang="scss">
textarea {
  resize: none;
  border: none !important;

  &:focus {
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }

  /* Prevent mobile zoom on input focus */
  @media (max-width: 768px) {
    font-size: 16px !important; /* Prevents iOS zoom */
    transform: translateZ(0); /* Force hardware acceleration */
  }
}
.error-message {
  bottom: calc(100% + 0.5rem);
  z-index: 20;
}
.drag-over {
  border: 2px dashed #3b82f6 !important;
}
.image-preview-container {
  bottom: 100%;
  left: 0;
  margin-bottom: 0.5rem;
  z-index: 10;
}
</style>
